import { useMutation } from "@apollo/client";
import { InputControl } from "@aurem/components";
import { Button, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import { ContactSupportLink } from "components/ContactSupportLink";
import ROUTES from "const/routes";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { gql } from "types";
import * as z from "zod";

const REQUEST_PASSWORD_RESET = gql(/* GraphQL */ `
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`);

const ForgotPassword: FC = () => {
  const { t } = useI18n();
  const [success, setSuccess] = useState(false);
  const [requestReset, { error, loading }] = useMutation(
    REQUEST_PASSWORD_RESET,
  );

  const formSchema = z.object({
    email: z
      .string()
      .nonempty(t("apps.admin.validation.email.required"))
      .email({ message: t("apps.admin.validation.email.format") }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const { data } = await requestReset({ variables: values });
    if (data?.requestPasswordReset) {
      setSuccess(true);
    }
  };

  return (
    <FormProvider {...form}>
      <AuthContainer platform="admin">
        {!success && (
          <>
            <div>
              <h3 className="font-medium text-2xl">
                {t("apps.admin.pages.forgotPassword.page.title")}
              </h3>
              <Text size="sm" className="mt-2">
                {t("apps.admin.pages.forgotPassword.page.description")}
              </Text>
            </div>

            <form
              className="flex flex-col gap-6"
              noValidate
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex flex-col gap-5">
                <InputControl
                  data-e2e-id="forgotPassword__input__email"
                  label={t("apps.admin.pages.forgotPassword.page.email.label")}
                  name="email"
                  required
                />
                {error && (
                  <Text size="sm" weight="medium" className="text-danger-600">
                    {t("apps.admin.pages.forgotPassword.page.error")}
                  </Text>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <Button
                  data-e2e-id="forgotPassword__button__submit"
                  disabled={loading}
                  type="submit"
                >
                  {t("apps.admin.pages.forgotPassword.page.submit")}
                </Button>
                <Button
                  as={Link}
                  className="justify-center"
                  to={"/" + ROUTES.SIGN_IN}
                  variant="outlined"
                  color="neutral"
                >
                  {t("apps.admin.pages.forgotPassword.page.cancel")}
                </Button>
              </div>
            </form>
          </>
        )}

        {success && (
          <>
            <h3 className="text-2xl">
              {t("apps.admin.pages.forgotPassword.page.success.title")}
            </h3>

            <Text size="sm">
              {t("apps.admin.pages.forgotPassword.page.success.description")}
            </Text>
          </>
        )}

        <ContactSupportLink />
      </AuthContainer>
    </FormProvider>
  );
};

export default ForgotPassword;
